import { AppDispatch } from '@/redux/store';
import { createBoundingBoxString } from './createBoundingBoxString';
import { fetchCatalogsNearMeByBounds } from '@/redux/modules/auctionsNearMe/auctionsNearMe.actions';
import { GeoArea } from '@/types/GeoArea';
import { MapBounds } from '../components/MapView/components/GoogleMap/AuctionsNearMeGoogleMap';
import { useEffect } from 'react';

type params = {
    currentActiveBoundsRef: { current: MapBounds | null };
    currentPage: number;
    customSearchBounds: MapBounds | null;
    defaultBounds: MapBounds;
    dispatch: AppDispatch;
    geoArea: GeoArea | null;
    isGeocoding: boolean;
    lastFetchParamsRef: { current: { bounds: string; page: number; pageSize: number } | null };
    pageSize: number;
    prevPaginationRef: { current: { page: number; pageSize: number } };
    // TODO: Remove feature flag once new auctions near me page is fully deployed
    // @see https://liveauctioneers.atlassian.net/browse/SG-229
    useNewAuctionsNearMePage: boolean;
};

/**
 * Custom hook to fetch catalogs when pagination or page size changes
 * Does NOT run on initial mount - map/search/geoArea handle initial fetch
 */
export const usePaginatedCatalogFetch = ({
    currentActiveBoundsRef,
    currentPage,
    customSearchBounds,
    defaultBounds,
    dispatch,
    geoArea,
    isGeocoding,
    lastFetchParamsRef,
    pageSize,
    prevPaginationRef,
    useNewAuctionsNearMePage,
}: params) => {
    useEffect(() => {
        if (!useNewAuctionsNearMePage) {
            return;
        }

        // Skip if page AND pageSize haven't changed (initial mount or other re-renders)
        if (prevPaginationRef.current.page === currentPage && prevPaginationRef.current.pageSize === pageSize) {
            return;
        }

        // Skip if we're currently geocoding (will fetch after geocoding completes)
        if (isGeocoding) {
            return;
        }

        // Determine bounds to use in priority order:
        // 1. currentActiveBoundsRef (from previous map/search action)
        // 2. customSearchBounds (from search input)
        // 3. geoArea bounds (from URL params)
        // 4. defaultBounds (based on locale)
        let bounds: MapBounds;
        if (currentActiveBoundsRef.current) {
            bounds = currentActiveBoundsRef.current;
        } else if (customSearchBounds) {
            bounds = customSearchBounds;
        } else if (geoArea && geoArea.northEastLatitude) {
            bounds = {
                northEast: { lat: geoArea.northEastLatitude, lng: geoArea.northEastLongitude },
                southWest: { lat: geoArea.southWestLatitude, lng: geoArea.southWestLongitude },
            };
        } else {
            bounds = defaultBounds;
        }

        const insideBoundingBox = createBoundingBoxString(bounds);

        // Check if params actually changed to avoid duplicate fetches
        const paramsChanged =
            !lastFetchParamsRef.current ||
            lastFetchParamsRef.current.bounds !== insideBoundingBox ||
            lastFetchParamsRef.current.page !== currentPage ||
            lastFetchParamsRef.current.pageSize !== pageSize;

        if (!paramsChanged) {
            prevPaginationRef.current = { page: currentPage, pageSize: pageSize };
            return;
        }

        // Update refs
        prevPaginationRef.current = { page: currentPage, pageSize: pageSize };
        lastFetchParamsRef.current = { bounds: insideBoundingBox, page: currentPage, pageSize: pageSize };

        dispatch(
            fetchCatalogsNearMeByBounds({
                insideBoundingBox,
                page: currentPage,
                pageSize: pageSize,
            })
        );
    }, [
        useNewAuctionsNearMePage,
        currentPage,
        pageSize,
        customSearchBounds,
        geoArea,
        isGeocoding,
        defaultBounds,
        dispatch,
        currentActiveBoundsRef,
        lastFetchParamsRef,
        prevPaginationRef,
    ]);
};
